import { useState, useMemo, useEffect, useCallback } from "react";
import { venues } from "../data";
import {
  DAYS, MONTHS, API,
  toLocalDateStr, getDaysInMonth, getFirstDay, isSameDay, getWeekDates, workshopsForDate,
} from "./calendarUtils";
import { PasswordModal, AdminEditPanel, AdminEventModal } from "./AdminComponents";
import { EventModal, WorkshopBookingModal } from "./BookingComponents";
import { MonthView, WeekView, DayView } from "./CalendarViews";
import { WorkshopBanner } from "./CalendarPills";
import "./YogaCalendar.css";

export default function YogaCalendar({ initialAdminMode = false, hideAdmin = false }) {
  const today = new Date();

  const [viewMode, setViewMode]     = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 480 ? "week" : "month"
  );
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [focusDay, setFocusDay]       = useState(today);
  const [classes, setClasses]         = useState([]);
  const [privateBookings, setPrivateBookings] = useState([]);
  const [cancelledDates, setCancelledDates]   = useState(new Set());
  const [dateOverrides, setDateOverrides]     = useState(new Map());
  const [workshops, setWorkshops]     = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [adminMode]                   = useState(initialAdminMode);
  const [showPwModal, setShowPwModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingClassFromModal, setEditingClassFromModal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate]   = useState(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 480
  );

  const year        = currentDate.getFullYear();
  const month       = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDay(year, month);

  const fetchPrivateBookings = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/bookings/private`);
      const data = await res.json();
      setPrivateBookings(data.bookings ?? []);
    } catch (e) {
      console.error("Failed to load private bookings:", e);
    }
  }, []);

  useEffect(() => {
    async function fetchSchedule() {
      try {
        const [classRes, wsRes, cancelRes, overrideRes, privateBkRes] = await Promise.all([
          fetch(`${API}/schedule/classes`),
          fetch(`${API}/schedule/workshops`),
          fetch(`${API}/schedule/cancellations`),
          fetch(`${API}/schedule/overrides`),
          fetch(`${API}/bookings/private`),
        ]);
        const classData    = await classRes.json();
        const wsData       = await wsRes.json();
        const cancelData   = await cancelRes.json();
        const overrideData = await overrideRes.json();
        const privateBkData = await privateBkRes.json();

        setClasses(classData.classes ?? []);
        setWorkshops(wsData.workshops ?? []);
        setPrivateBookings(privateBkData.bookings ?? []);
        setCancelledDates(new Set(
          (cancelData.cancellations ?? []).map(c => `${c.classId}__${c.date}`)
        ));
        setDateOverrides(new Map(
          (overrideData.overrides ?? []).map(o => [`${o.classId}__${o.date}`, { venue: o.venue }])
        ));
      } catch (e) {
        console.error("Failed to load schedule:", e);
      } finally {
        setScheduleLoading(false);
      }
    }
    fetchSchedule();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const handler = e => {
      setIsMobile(e.matches);
      setViewMode(v => {
        if (e.matches && v === "month") return "week";
        if (!e.matches && v === "week") return "month";
        return v;
      });
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const prevMonth = () => setCurrentDate(new Date(year,month-1,1));
  const nextMonth = () => setCurrentDate(new Date(year,month+1,1));

  const weekDates = useMemo(() => {
    if (isMobile) {
      return [0,1].map(offset => {
        const d = new Date(focusDay);
        d.setDate(focusDay.getDate() + offset);
        return d;
      });
    }
    return getWeekDates(focusDay);
  }, [focusDay, isMobile]);

  const weekStep = isMobile ? 2 : 7;
  const prevWeek = () => { const d=new Date(focusDay); d.setDate(d.getDate()-weekStep); setFocusDay(d); };
  const nextWeek = () => { const d=new Date(focusDay); d.setDate(d.getDate()+weekStep); setFocusDay(d); };
  const prevDay  = () => { const d=new Date(focusDay); d.setDate(d.getDate()-1); setFocusDay(d); };
  const nextDay  = () => { const d=new Date(focusDay); d.setDate(d.getDate()+1); setFocusDay(d); };

  const calendarCells = useMemo(() => {
    const cells = [];
    for(let i=0;i<firstDay;i++) cells.push(null);
    for(let d=1;d<=daysInMonth;d++) cells.push(new Date(year,month,d));
    while(cells.length%7!==0) cells.push(null);
    return cells;
  }, [year,month,daysInMonth,firstDay]);

  const getVisibleClasses = (date) => {
    const dow = date.getDay(), dateStr = toLocalDateStr(date);
    const regular = classes
      .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate))
      .map(c => {
        const key = `${c.id}__${dateStr}`;
        const override = dateOverrides.get(key);
        return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key), _overridden: !!override };
      });
    const privates = privateBookings
      .filter(b => b.date === dateStr)
      .map(b => {
        const timeRaw = b.classId.split("__")[2] ?? "";
        const time = timeRaw ? `${timeRaw.slice(0,2)}:${timeRaw.slice(2)}` : "";
        return { id: b._id, _id: b._id, classId: b.classId, title: "Private", time, duration: 60, _isPrivate: true, _cancelled: false };
      });
    return [...regular, ...privates];
  };

  const getPublicClasses = (date) => {
    const dow = date.getDay(), dateStr = toLocalDateStr(date);
    const regular = classes
      .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate))
      .map(c => {
        const key = `${c.id}__${dateStr}`;
        const override = dateOverrides.get(key);
        return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key), _isPrivate: !!c.isPrivate };
      });
    const privates = privateBookings
      .filter(b => b.date === dateStr)
      .map(b => {
        const timeRaw = b.classId.split("__")[2] ?? "";
        const time = timeRaw ? `${timeRaw.slice(0,2)}:${timeRaw.slice(2)}` : "";
        return { id: b._id, title: "Private", time, _isPrivate: true, _cancelled: false };
      });
    return [...regular, ...privates];
  };

  const closeModal = () => { setSelectedEvent(null); setSelectedDate(null); setSelectedWorkshop(null); };

  const handleSaveClass = async (form) => {
    const isNew = !classes.find(c => c.id === form.id);
    if (isNew) {
      const newClass = { ...form, id: `class_${Date.now()}`, startDate: toLocalDateStr(new Date()) };
      await fetch(`${API}/schedule/classes`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify(newClass) });
      setClasses(prev => [...prev, newClass]);
    } else {
      await fetch(`${API}/schedule/classes/${form.id}`, { method:"PATCH", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify(form) });
      setClasses(prev => prev.map(c => c.id === form.id ? {...form} : c));
    }
  };

  const handleDeleteClass = async (id) => {
    await fetch(`${API}/schedule/classes/${id}`, { method:"DELETE", headers:{"Authorization":"Bearer admin123"} });
    setClasses(prev => prev.filter(c => c.id !== id));
  };

  const handleSaveWorkshop = async (form) => {
    const isNew = !workshops.find(w => w.id === form.id);
    if (isNew) {
      const newWS = { ...form, id: `ws_${Date.now()}` };
      await fetch(`${API}/schedule/workshops`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify(newWS) });
      setWorkshops(prev => [...prev, newWS]);
    } else {
      await fetch(`${API}/schedule/workshops/${form.id}`, { method:"PATCH", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify(form) });
      setWorkshops(prev => prev.map(w => w.id === form.id ? {...form} : w));
    }
  };

  const handleDeleteWorkshop = async (id) => {
    await fetch(`${API}/schedule/workshops/${id}`, { method:"DELETE", headers:{"Authorization":"Bearer admin123"} });
    setWorkshops(prev => prev.filter(w => w.id !== id));
  };

  const handleCancelDate = async (classId, dateStr) => {
    await fetch(`${API}/schedule/cancellations`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify({classId, date:dateStr}) });
    setCancelledDates(prev => { const n=new Set(prev); n.add(`${classId}__${dateStr}`); return n; });
    await fetch(`${API}/bookings/cancel-class`, { method:"PATCH", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify({classId, date:dateStr}) });
    closeModal();
  };

  const handleRestoreDate = async (classId, dateStr) => {
    await fetch(`${API}/schedule/cancellations`, { method:"DELETE", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify({classId, date:dateStr}) });
    setCancelledDates(prev => { const n=new Set(prev); n.delete(`${classId}__${dateStr}`); return n; });
    closeModal();
  };

  const handleSaveDateOverride = async (classId, dateStr, overrides) => {
    await fetch(`${API}/schedule/overrides`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer admin123"}, body:JSON.stringify({classId, date:dateStr, ...overrides}) });
    setDateOverrides(prev => { const next=new Map(prev); next.set(`${classId}__${dateStr}`, overrides); return next; });
  };

  const handleEditRecurring = (event) => {
    setShowEditPanel(true);
    setEditingClassFromModal(event);
  };

  const renderWorkshopBanners = (date) => {
    const dateStr = toLocalDateStr(date);
    return workshopsForDate(dateStr, workshops).map(w => (
      <WorkshopBanner key={w.id} workshop={w} dateStr={dateStr} onClick={setSelectedWorkshop} adminMode={adminMode} />
    ));
  };

  const sharedViewProps = {
    today, adminMode, getVisibleClasses, getPublicClasses,
    workshops, setSelectedEvent, setSelectedDate, setSelectedWorkshop,
  };

  return (
    <>
      <div className="cal-wrap">
        {scheduleLoading && <p className="cal-loading">Loading schedule…</p>}

        {/* Header */}
        <div className="cal-header">
          <h2 className="cal-title">
            {viewMode==="month"
              ? <><em>{MONTHS[month]}</em> {year}</>
              : viewMode==="week"
              ? isMobile
                ? <><em>{weekDates[0].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} &amp; <em>{weekDates[1].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[1].toLocaleDateString("en-US",{day:"numeric"})}</>
                : <><em>Week of</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})}</>
              : <><em>{DAYS[focusDay.getDay()]}</em> {focusDay.toLocaleDateString("en-US",{month:"long",day:"numeric"})}</>
            }
          </h2>
          <div className="cal-nav">
            <button className="nav-btn" onClick={viewMode==="month"?prevMonth:viewMode==="week"?prevWeek:prevDay}>‹</button>
            <span className="nav-label">
              {viewMode==="month"
                ? `${MONTHS[month].slice(0,3)} ${year}`
                : viewMode==="week"
                ? isMobile
                  ? `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[1].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                  : `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[6].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                : focusDay.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})
              }
            </span>
            <button className="nav-btn" onClick={viewMode==="month"?nextMonth:viewMode==="week"?nextWeek:nextDay}>›</button>
          </div>
          <div className="cal-header-right">
            <div className="view-toggle">
              <button data-view="month" className={`toggle-btn ${viewMode==="month"?"active":""}`} onClick={()=>setViewMode("month")}>Month</button>
              <button data-view="week"  className={`toggle-btn ${viewMode==="week" ?"active":""}`} onClick={()=>setViewMode("week")}>Week</button>
              <button data-view="day"   className={`toggle-btn ${viewMode==="day"  ?"active":""}`} onClick={()=>setViewMode("day")}>Day</button>
            </div>
            {adminMode
              ? <button className="admin-add-btn" onClick={()=>setShowEditPanel(true)}>Edit Schedule</button>
              : !hideAdmin && <button className="admin-login-btn" onClick={()=>setShowPwModal(true)}>Admin</button>
            }
          </div>
        </div>

        {viewMode==="month" && (
          <MonthView {...sharedViewProps}
            calendarCells={calendarCells}
            renderWorkshopBanners={renderWorkshopBanners} />
        )}
        {viewMode==="week" && (
          <WeekView {...sharedViewProps} weekDates={weekDates} />
        )}
        {viewMode==="day" && (
          <DayView {...sharedViewProps} focusDay={focusDay} />
        )}
        
        {/* Legend */}
        <div className="cal-legend">
          <div className="legend-item"><div className="legend-dot dot-studio"/><span>In Studio</span></div>
          <div className="legend-item"><div className="legend-dot dot-online"/><span>Online</span></div>
          <div className="legend-item"><div className="legend-dot dot-hybrid"/><span>Hybrid</span></div>
          <div className="legend-item"><div className="legend-dot dot-workshop"/><span>Workshop</span></div>
          {adminMode && <div className="legend-item"><div className="legend-dot dot-cancelled"/><span>Cancelled</span></div>}
        </div>
        <div className="yc-powered-by">
          <a href="https://florium.live" target="_blank" rel="noopener noreferrer" className="yc-powered-link">
            <span className="yc-powered-text">powered by</span> <span className="yc-powered-name">florium.</span>
          </a>
        </div>
      </div>
        {/* </div> */}
      {showPwModal && (
        <PasswordModal
          onSuccess={() => { setShowPwModal(false); window.open('/admin?admin=true', '_blank'); }}
          onClose={() => setShowPwModal(false)} />
      )}

      {showEditPanel && (
        <AdminEditPanel
          classes={classes} workshops={workshops} venueList={venues}
          initialEditClass={editingClassFromModal}
          onSaveClass={handleSaveClass}       onDeleteClass={handleDeleteClass}
          onSaveWorkshop={handleSaveWorkshop} onDeleteWorkshop={handleDeleteWorkshop}
          onClose={()=>{ setShowEditPanel(false); setEditingClassFromModal(null); }} />
      )}

      {selectedWorkshop && !adminMode && (
        <WorkshopBookingModal workshop={selectedWorkshop} onClose={closeModal} />
      )}

      {selectedWorkshop && adminMode && (()=>{ setShowEditPanel(true); setSelectedWorkshop(null); return null; })()}

      {selectedEvent && selectedDate && (
        adminMode
          ? <AdminEventModal event={selectedEvent} date={selectedDate}
              cancelledDates={cancelledDates} dateOverrides={dateOverrides}
              venueList={venues}
              onCancelDate={handleCancelDate} onRestoreDate={handleRestoreDate}
              onSaveDateOverride={handleSaveDateOverride}
              onEditRecurring={handleEditRecurring}
              onDone={fetchPrivateBookings}
              onClose={closeModal} />
          : <EventModal event={selectedEvent} date={selectedDate} onClose={closeModal} />
      )}
    </>     
  );
}

// needed by renderWorkshopBanners inside YogaCalendar
